import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import { PIGLET_PERIOD_OPTIONS } from '../../models/piglet-registration';

export interface PigletPeriodSelectProps {
  value: number;
  onChange: (periodWeeks: number) => void;
  disabled?: boolean;
}

/**
 * A shadcn-style select rather than the MUI outlined one, so the control matches the cards it sits above
 * instead of the v4 form fields on the rest of the app. Hand-rolled because @radix-ui/react-select is not
 * a dependency here and five static options do not justify adding one.
 *
 * Every native button style is reset explicitly: this project loads Tailwind's theme and utilities without
 * preflight, so a bare <button> keeps the browser's grey face and border.
 */
export default function PigletPeriodSelect({ value, onChange, disabled }: PigletPeriodSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('f-piglets-reg-period')}
        onClick={() => setOpen((isOpen) => !isOpen)}
        className="flex h-9 w-44 cursor-pointer appearance-none items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-900 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-default disabled:opacity-50"
      >
        <span className="truncate">{t('f-piglets-reg-period-weeks', { weeks: value })}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 z-50 mt-1 w-44 overflow-hidden rounded-md border border-gray-200 bg-white p-1 shadow-md"
        >
          {PIGLET_PERIOD_OPTIONS.map((weeks) => {
            const selected = weeks === value;

            return (
              <button
                key={weeks}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(weeks);
                  setOpen(false);
                }}
                className={`flex w-full cursor-pointer appearance-none items-center gap-2 rounded-sm border-0 bg-transparent py-1.5 pr-2 pl-2 text-left text-sm transition-colors hover:bg-gray-100 ${
                  selected ? 'text-gray-900' : 'text-gray-700'
                }`}
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                  {selected && <Check className="h-4 w-4 text-indigo-600" aria-hidden="true" />}
                </span>
                <span className="truncate">{t('f-piglets-reg-period-weeks', { weeks })}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
